import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { uuidv7 } from "@delta/pos-core";
import { useLang } from "../../i18n";
import { Icon } from "../../components/Icon";
import type { IconName } from "../../components/icons";
import { useToast } from "../../components/Toast";
import {
  agentCall, agentUrl, claimRegister, classifySaveAnswer, createUnconfirmed, deleteUnconfirmed, loadUnconfirmed, samePayload, saveToken,
  savedToken, updateUnconfirmed, type AgentSale, type AgentStatus, type Ownership, type TrainingPayload, type UnconfirmedSale,
  type UnconfirmedState
} from "../../lib/agent";
import { CAT_ICON, TRAINING_ITEMS, lineCents, money } from "./training-items";
import "../../styles/pos.css";

// The training register (offline POS design, section 11): the approved prototype's POS sale screen (pos.js
// renderSale) on top of the local Delta POS agent. Training mode only: sales are synthetic, carry no tax, payment,
// stock or accounting effect, and are not invoices. Signing and printing are shown as unavailable.
//
// Duplicate protection: a sale's id is made once, when Save is first pressed, and kept durably (with its lines) before
// it is sent; if it cannot be kept, nothing is sent. A repeated click is ignored while a save is in flight; a retry
// after a lost or unconfirming answer, or after a reload, sends the same id and lines, and the agent stores a sale id
// once. The cart stays locked until the agent confirms this exact sale and its record is removed, or until an answer
// proves it was not saved.
//
// One tab owns the register (a Web Lock): only it sells, recovers and touches the kept records. Another tab is
// view-only until the owner closes or crashes; it then takes over, reads the records and reconciles them.

type View = "sale" | "sales" | "register";
type ListTab = "Pending" | "Synced" | "Failed";
interface CartLine { itemId: string; qty: number }

const ERR: Record<string, string> = {
  pairing_invalid: "regErrPairing", too_many_attempts: "regErrTooMany", activation_invalid: "regErrActivation", recovery_invalid: "regErrRecovery",
  invalid_credentials: "signInFailed", grant_denied: "regErrGrantDenied", grant_required: "regErrGrantRequired", grant_exhausted: "regErrGrantExhausted",
  device_deactivated: "regErrDeactivated", device_suspended: "regErrSuspended", credential_unavailable: "regErrCredential", sale_conflict: "regErrConflict",
  offline: "regErrOffline", not_activated: "regErrNotActivated", validation: "regErrValidation", mobile: "mobileFormat"
};

export function TrainingRegister() {
  const { t, lang, setLang, L } = useLang();
  const toast = useToast();
  const [view, setView] = useState<View>("sale");
  const [listTab, setListTab] = useState<ListTab>("Pending");
  const [token, setTokenState] = useState<string | null>(savedToken);
  const [status, setStatus] = useState<AgentStatus | null>(null);
  const [reachable, setReachable] = useState<boolean | null>(null);
  const [sales, setSales] = useState<AgentSale[]>([]);
  const [cart, setCart] = useState<CartLine[]>([]);
  const [ownership, setOwnership] = useState<Ownership>("checking");
  const owner = ownership === "owner";
  const [unconfirmed, setUnconfirmed] = useState<UnconfirmedSale | null>(null);
  // The sale this tab is working on, for callbacks that finish late: they act only if it is still their sale.
  const current = useRef<UnconfirmedSale | null>(null);
  const [storeReady, setStoreReady] = useState(false);
  const [storageError, setStorageError] = useState<null | "regErrStorage" | "regErrStorageChange">(null);
  const [saving, setSaving] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);
  const savingRef = useRef(false);
  useEffect(() => { document.title = `${t("regTitle")} · Delta`; }, [t]);

  const setToken = (v: string | null) => { saveToken(v); setTokenState(v); };
  const errText = (code: string | null, status: number | null) => status === null ? t("regErrNoAnswer") : t(ERR[code ?? ""] ?? "serverError");

  const refresh = useCallback(async () => {
    const h = await agentCall<{ ok: boolean }>("GET", "/health", undefined, null);
    setReachable(h.status === 200);
    if (h.status !== 200 || !token) { setStatus(null); return; }
    const s = await agentCall<AgentStatus>("GET", "/status", undefined, token);
    if (s.status === 401 || s.status === 403) { setToken(null); setStatus(null); return; }
    if (s.body) setStatus(s.body);
    const l = await agentCall<AgentSale[]>("GET", "/sales?limit=200", undefined, token);
    if (l.body) setSales(l.body);
  }, [token]);
  useEffect(() => {
    void refresh();
    const id = setInterval(() => { void refresh(); }, 3000);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => claimRegister(setOwnership), []);

  const setCurrent = (u: UnconfirmedSale | null) => { current.current = u; setUnconfirmed(u); };
  /** Replaces the sale on screen only if it is still saleId: a late callback for an earlier sale changes nothing. */
  const setIfCurrent = (saleId: string, u: UnconfirmedSale | null) => {
    if (current.current?.saleId !== saleId) return false;
    setCurrent(u);
    return true;
  };
  /** Reads the kept records (owner only): the oldest open one is the sale on screen. */
  async function loadKept() {
    const r = await loadUnconfirmed();
    if (!r.ok) { setStorageError("regErrStorage"); setCurrent(null); return; }
    // A record still "pending" was sent by an earlier page or tab whose answer never came here: it may be saved.
    const u = r.sales[0] ?? null;
    setCurrent(u && u.state === "pending" ? { ...u, state: "uncertain" } : u);
  }

  // Only the owner reads the records; a tab that takes over reads them then. Until they are read, nothing is saved.
  useEffect(() => {
    if (!owner) { setStoreReady(false); setCurrent(null); return; }
    void loadKept().then(() => setStoreReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner]);

  // A sale that may be saved: the agent's list shows it once it is there.
  useEffect(() => {
    if (!owner || !unconfirmed || savingRef.current || unconfirmed.state === "conflict" || unconfirmed.state === "saved") return;
    const found = sales.find((s) => s.saleId === unconfirmed.saleId);
    if (found) void settle(unconfirmed, found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sales]);
  // After a reload or a takeover with a kept sale, ask the agent about it directly; finish an interrupted cleanup.
  useEffect(() => {
    // Not while a save is in flight: that save settles its own sale.
    if (!owner || !storeReady || !unconfirmed || savingRef.current) return;
    if (unconfirmed.state === "saved") void cleanUp(unconfirmed.saleId);
    else if (token) void reconcile(unconfirmed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, storeReady, token, unconfirmed?.saleId]);
  // A confirmed sale whose record could not be removed yet: try again, and stay locked until it is gone.
  useEffect(() => {
    if (!owner || unconfirmed?.state !== "saved") return;
    const id = setInterval(() => { void cleanUp(unconfirmed.saleId); }, 2000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, unconfirmed?.saleId, unconfirmed?.state]);

  /** The agent holds a sale under the kept id: this sale if the lines match, otherwise a conflict to settle by hand. */
  async function settle(u: UnconfirmedSale, found: AgentSale) {
    if (samePayload(found.payload, u.payload)) await confirmSaved(u, found, false);
    else await keep(u, "conflict", "sale_conflict");
  }
  /** Looks the kept sale up in the agent. Only an answer naming it settles anything; else it stays as it is. */
  async function reconcile(u: UnconfirmedSale) {
    const r = await agentCall<AgentSale>("GET", `/sales/${u.saleId}`);
    if (r.status === 200 && r.body?.saleId === u.saleId) await settle(u, r.body);
  }
  /**
   * Records a new state for this sale's record, if it still exists. The screen follows only a state that was
   * written: if writing fails, the record keeps its earlier, more cautious state, and so does the screen.
   */
  async function keep(u: UnconfirmedSale, state: UnconfirmedState, code: string | null) {
    const r = await updateUnconfirmed(u.saleId, state, code);
    if (r === "ok") setIfCurrent(u.saleId, { ...u, state, code });
    else if (r === "failed") setStorageError("regErrStorageChange");
    return r;
  }
  /** Removes this sale's record; only then is the register unlocked. The next kept sale, if any, comes up. */
  async function cleanUp(saleId: string) {
    if (!(await deleteUnconfirmed(saleId))) { setStorageError("regErrStorageChange"); return false; }
    if (current.current?.saleId === saleId) {
      setStorageError(null);
      setCart([]);
      await loadKept();
    }
    return true;
  }

  async function confirmSaved(u: UnconfirmedSale, s: AgentSale, fresh: boolean) {
    if (current.current?.saleId !== u.saleId) { await deleteUnconfirmed(u.saleId); return; }
    toast(t(fresh ? "regSaved" : "regSavedConfirmed", { n: s.localNumber ?? String(s.deviceSeq) }));
    // Saved: the register stays locked to this sale until its record is gone, so nothing can start over it.
    await keep(u, "saved", null);
    setIfCurrent(u.saleId, { ...u, state: "saved", code: null });
    await cleanUp(u.saleId);
  }

  const grantLive = !!status?.grant && Date.parse(status.grant.expiresAt) > Date.now() && status.grant.salesUsed < status.grant.maxSales;
  const step: "agent" | "pair" | "activate" | "recover" | "signin" | "ready" =
    reachable === false ? "agent" : !token ? "pair" : !status ? "agent" : !status.activated ? "activate"
      : status.credential.state === "unavailable" ? "recover" : !grantLive ? "signin" : "ready";

  // ---------------------------------------------------------------- cart
  const locked = !owner || !!unconfirmed || !storeReady;
  const lines: CartLine[] = unconfirmed ? unconfirmed.payload.lines.map((l) => ({ itemId: l.itemId, qty: Number(l.qty) })) : cart;
  const item = (id: string) => TRAINING_ITEMS.find((i) => i.id === id)!;
  const total = lines.reduce((s, l) => s + lineCents(String(l.qty), item(l.itemId).price), 0);
  const add = (id: string) => { if (locked) return; setCart((c) => c.some((l) => l.itemId === id) ? c.map((l) => l.itemId === id ? { ...l, qty: Math.min(l.qty + 1, 999) } : l) : [...c, { itemId: id, qty: 1 }]); };
  const setQty = (id: string, q: number) => { if (locked) return; setCart((c) => q < 1 ? c.filter((l) => l.itemId !== id) : c.map((l) => l.itemId === id ? { ...l, qty: Math.min(q, 999) } : l)); };

  async function save() {
    if (!owner || savingRef.current || !storeReady || unconfirmed?.state === "conflict" || unconfirmed?.state === "saved") return;
    savingRef.current = true;
    setSaving(true);
    try {
      let u = current.current;
      const firstAttempt = !u;
      if (!u) {
        if (!cart.length) return;
        const payload: TrainingPayload = { version: 1, mode: "training", lines: cart.map((l) => ({ itemId: l.itemId, qty: String(l.qty), unitPrice: item(l.itemId).price })) };
        const fresh: UnconfirmedSale = { saleId: uuidv7(Date.now(), crypto), payload, createdAt: new Date().toISOString(), state: "pending" };
        // Kept before it is sent, so a reload, a lost answer or a takeover sends this same sale again. If it cannot be
        // kept, it is not sent: the cart stays, and nothing exists that a retry could duplicate. It is kept only if
        // no other sale is open.
        const k = await createUnconfirmed(fresh);
        if (k === "busy") { await loadKept(); return; }
        if (k === "failed") { setStorageError("regErrStorage"); return; }
        setStorageError(null);
        setCurrent(fresh);
        u = fresh;
      }
      const r = await agentCall<AgentSale>("POST", "/sales", { saleId: u.saleId, payload: u.payload });
      const c = classifySaveAnswer(r, u, firstAttempt);
      if (c.kind === "saved") { await confirmSaved(u, c.sale, c.fresh); return; }
      if ((await keep(u, c.kind, c.code)) !== "ok") return;
      // The answer did not settle it: ask the agent whether it has this sale.
      if (c.kind === "uncertain") await reconcile({ ...u, state: c.kind, code: c.code });
    } finally {
      savingRef.current = false;
      setSaving(false);
      void refresh();
    }
  }
  async function discardRefused() {
    // Only a sale the agent's answer proves is not saved can be changed. Its record is removed first: if that
    // fails, it stays locked, since a kept record would be sent again after a reload.
    const u = current.current;
    if (!owner || u?.state !== "refused") return;
    if (!(await deleteUnconfirmed(u.saleId))) { setStorageError("regErrStorageChange"); return; }
    if (!setIfCurrent(u.saleId, null)) return;
    setStorageError(null);
    setCart(u.payload.lines.map((l) => ({ itemId: l.itemId, qty: Number(l.qty) })));
    await loadKept();
  }
  async function keepStoredSale() {
    // A conflict: the agent holds a different sale under this id. That stored sale stands; this one is not sent
    // again under a new id, so nothing is added. Only the local record is removed.
    const u = current.current;
    if (!owner || u?.state !== "conflict") return;
    if (!(await cleanUp(u.saleId))) return;
    toast(t("regConflictKept"));
  }

  async function retrySale(id: string) {
    if (!owner || retrying) return;
    setRetrying(id);
    const r = await agentCall<AgentSale>("POST", `/sales/${id}/retry`, {});
    setRetrying(null);
    toast(r.status === 200 ? t("regRetryQueued") : errText(r.code, r.status));
    void refresh();
  }
  async function syncNow() {
    if (!owner) return;
    const r = await agentCall("POST", "/sync", {});
    toast(r.status === 200 ? t("regSyncDone") : errText(r.code, r.status));
    void refresh();
  }

  // ---------------------------------------------------------------- render
  const tabs: [View, IconName, string][] = [["sale", "cart", t("regTabSale")], ["sales", "receipt", t("regTabSales")], ["register", "settings", t("regTabRegister")]];
  const counts = status?.sales;
  return (
    <div className="pos reg-root" data-testid="register">
      <header className="pos-top">
        <div className="pos-brand">
          <span className="wordmark"><img className="mark" src="/delta-mark.svg" alt="" /><span><span className="wm-text" style={{ color: "#fff" }}>{lang === "ar" ? "دلتا" : "DELTA"}</span></span></span>
          <span className="pos-badge training" data-testid="training-badge">{t("regTrainingMode")}</span>
        </div>
        <nav className="pos-tabs" aria-label={t("regTitle")}>
          {tabs.map(([v, icn, label]) => (
            <a key={v} href="#" className={view === v ? "active" : ""} aria-current={view === v ? "page" : undefined} onClick={(e) => { e.preventDefault(); setView(v); }}>
              <Icon name={icn} /><span>{label}</span>
            </a>
          ))}
        </nav>
        <div className="pos-tools">
          <span className="pos-chip" data-testid="agent-chip"><Icon name={reachable ? "check" : "alert"} /><span className="chip-text">{t("regAgent")}</span> <b>{reachable === null ? "…" : reachable ? t("regConnected") : t("regNotReachable")}</b></span>
          {status?.grant && grantLive && <span className="pos-chip"><Icon name="user" /><b>{status.grant.userName}</b></span>}
          <button className="pos-icon-btn pos-lang" onClick={() => setLang(lang === "ar" ? "en" : "ar")}><Icon name="globe" /><span>{t("switchLang")}</span></button>
        </div>
      </header>
      <div className="reg-notes">
      {!owner && ownership !== "checking" && (
        <div className="reg-blocked" role="alert" data-testid="register-blocked">
          <Icon name="lock" /><span>{t(ownership === "unsupported" ? "regLocksUnsupported" : "regOtherTab")}</span>
        </div>
      )}
      <div className="train-banner" role="note" data-testid="training-banner">
        <Icon name="alert" />
        <span><b>{t("regTrainingMode")}.</b> {t("regTrainingBody")}</span>
        <span className="pill warn" data-testid="signing-unavailable">{t("regSigningUnavailable")}</span>
        <span className="pill warn" data-testid="printing-unavailable">{t("regPrintingUnavailable")}</span>
      </div>
      </div>
      <main className="pos-main">
        {view === "register" && <RegisterView status={status} reachable={reachable} step={step} token={token} readOnly={!owner} setToken={setToken} refresh={refresh} errText={errText} syncNow={syncNow} />}
        {view === "sales" && (
          <div className="reg-lists">
            <div className="card">
              <div className="card-head"><h2>{t("regTabSales")}</h2>
                <button className="btn btn-sm" onClick={() => { void syncNow(); }} disabled={!owner || step === "agent" || step === "pair"}><Icon name="refresh" />{t("regSyncNow")}</button></div>
              <div className="tabs" role="tablist">
                {(["Pending", "Synced", "Failed"] as const).map((k) => (
                  <button key={k} role="tab" aria-selected={listTab === k} className={`tab ${listTab === k ? "on" : ""}`} data-testid={`list-${k}`} onClick={() => setListTab(k)}>
                    {t(`regList${k}`)}<span className="count num">{counts ? counts[k.toLowerCase() as "pending" | "synced" | "failed"] : 0}</span>
                  </button>
                ))}
              </div>
              <SalesTable sales={sales.filter((s) => s.status === listTab)} retrying={retrying} readOnly={!owner} onRetry={retrySale} item={item} />
            </div>
          </div>
        )}
        {view === "sale" && (step !== "ready" ? (
          <RegisterView status={status} reachable={reachable} step={step} token={token} readOnly={!owner} setToken={setToken} refresh={refresh} errText={errText} syncNow={syncNow} compact />
        ) : (
          <div className="pos-sale reg">
            <section className="pos-catalog">
              <div className="hint" style={{ margin: "0 4px 12px" }}>{t("regCatalogHint")}</div>
              <div className="prod-grid" data-testid="catalog">
                {TRAINING_ITEMS.map((it) => {
                  const inCart = lines.find((l) => l.itemId === it.id)?.qty ?? 0;
                  return (
                    <button key={it.id} className="prod" onClick={() => add(it.id)} disabled={locked} aria-label={L(it)} data-sku={it.sku}>
                      {inCart > 0 && <span className="prod-qty num">{inCart}</span>}
                      <span className={`prod-ico c-${it.cat}`}><Icon name={CAT_ICON[it.cat]} /></span>
                      <span className="prod-name">{L(it)}</span>
                      <span className="prod-sku num">{it.sku}</span>
                      <span className="prod-foot"><b className="num">{it.price}</b><span className="cur">{t("currency")}</span></span>
                    </button>
                  );
                })}
              </div>
            </section>
            <aside className="pos-cart" aria-label={t("regCurrentSale")}>
              <div className="cart-head">
                <div className="cart-title"><h2>{t("regCurrentSale")}</h2><span className="count num" data-testid="cart-count">{lines.length}</span>
                  <button className="btn btn-sm btn-ghost" onClick={() => setCart([])} disabled={locked || !cart.length}><Icon name="trash" />{t("regClear")}</button></div>
                <div className="hint">{t("regCashier", { x: status?.grant?.userName ?? "" })} · {t("regGrantUse", { n: status?.grant?.salesUsed ?? 0, m: status?.grant?.maxSales ?? 0 })}</div>
              </div>
              <div className="cart-lines" data-testid="cart-lines">
                {storageError && (
                  <div className="reg-unconfirmed" role="alert" data-testid="storage-error">
                    <Icon name="alert" /><span>{t(storageError)}</span>
                  </div>
                )}
                {unconfirmed && (
                  <div className="reg-unconfirmed" role="alert" data-testid="unconfirmed" data-state={unconfirmed.state}>
                    <Icon name="alert" />
                    <span>{{
                      pending: t("regUnconfirmedHelp"),
                      uncertain: t("regNoAnswerHelp"),
                      blocked: `${errText(unconfirmed.code ?? null, 0)} ${t("regBlockedHelp")}`,
                      refused: `${t("regRefused")} ${errText(unconfirmed.code ?? null, 0)}`,
                      conflict: `${t("regErrConflict")} ${t("regConflictHelp")}`,
                      saved: t("regCleanupPending")
                    }[unconfirmed.state]}</span>
                  </div>
                )}
                {!lines.length ? <div className="empty cart-empty"><Icon name="cart" /><div>{t("regEmptyCart")}</div></div> : lines.map((l) => {
                  const it = item(l.itemId);
                  return (
                    <div key={l.itemId} className="cl">
                      <div className="cl-main">
                        <div className="cl-name"><b>{L(it)}</b><span><span className="num">{it.price}</span> {t("currency")}</span></div>
                        <div className="stepper">
                          <button onClick={() => setQty(l.itemId, l.qty - 1)} disabled={locked} aria-label="−">−</button>
                          <input className="num" value={l.qty} readOnly aria-label={t("qty")} />
                          <button onClick={() => setQty(l.itemId, l.qty + 1)} disabled={locked} aria-label="+"><Icon name="plus" /></button>
                        </div>
                        <div className="cl-total num">{money(lineCents(String(l.qty), it.price))}</div>
                        <span />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="cart-foot">
                <div className="cart-totals">
                  <div className="ct-grand"><span>{t("regTrainingAmount")}</span><span><span className="num" data-testid="training-amount">{money(total)}</span> <span className="cur">{t("currency")}</span></span></div>
                  <div className="hint">{t("regNoTax")}</div>
                </div>
                <button className="btn btn-primary pay-btn" data-testid="save-sale" onClick={() => { void save(); }} disabled={!owner || saving || !storeReady || unconfirmed?.state === "conflict" || unconfirmed?.state === "saved" || (!unconfirmed && !cart.length)}>
                  <Icon name="check" /><span>{saving ? t("saving") : unconfirmed ? t("regSaveAgain") : t("regSave")}</span>
                </button>
                {owner && unconfirmed?.state === "refused" && <button className="btn btn-ghost" style={{ width: "100%", marginTop: 8 }} onClick={() => { void discardRefused(); }} data-testid="discard-refused">{t("regEditRefused")}</button>}
                {owner && unconfirmed?.state === "conflict" && <button className="btn btn-ghost" style={{ width: "100%", marginTop: 8 }} onClick={() => { void keepStoredSale(); }} data-testid="keep-stored">{t("regKeepStored")}</button>}
              </div>
            </aside>
          </div>
        ))}
      </main>
    </div>
  );
}

/** Dates in the interface language, Gregorian calendar and Latin digits, as elsewhere in Delta. */
const locale = (lang: string) => lang === "ar" ? "ar-SA-u-ca-gregory-nu-latn" : "en-GB";

function SalesTable({ sales, retrying, readOnly, onRetry, item }: { sales: AgentSale[]; retrying: string | null; readOnly: boolean; onRetry(id: string): void; item(id: string): { nameEn: string; nameAr: string } }) {
  const { t, L, lang } = useLang();
  if (!sales.length) return <div className="empty"><Icon name="receipt" /><div>{t("regListEmpty")}</div></div>;
  return (
    <div className="tbl-wrap">
      <table className="tbl pos-tbl" data-testid="sales-table">
        <thead><tr><th>{t("regLocalNumber")}</th><th>{t("regSavedAt")}</th><th>{t("regLines")}</th><th>{t("regTrainingAmount")}</th><th>{t("status")}</th><th>{t("regAttempts")}</th><th /></tr></thead>
        <tbody>
          {sales.map((s) => (
            <tr key={s.saleId} data-sale={s.saleId}>
              <td className="num">{s.localNumber ?? s.deviceSeq}</td>
              <td className="num">{new Date(s.deviceTime).toLocaleString(locale(lang))}</td>
              <td>{s.payload.lines.map((l) => `${L(item(l.itemId))} × ${l.qty}`).join(", ")}</td>
              <td className="num">{money(s.payload.lines.reduce((n, l) => n + lineCents(l.qty, l.unitPrice), 0))}</td>
              <td>
                <span className={`pill ${s.status === "Synced" ? "good" : s.status === "Failed" ? "bad" : "warn"}`}>{t(`regList${s.status}`)}</span>
                {s.reviewReasons.length > 0 && <div className="hint">{t("regReview")}: {s.reviewReasons.join(", ")}</div>}
                {s.status !== "Synced" && s.lastError && <div className="hint">{s.lastError}</div>}
              </td>
              <td className="num">{s.attempts}</td>
              <td>{s.status === "Failed" && <button className="btn btn-sm" disabled={readOnly || retrying !== null} onClick={() => onRetry(s.saleId)} data-testid="retry-sale"><Icon name="refresh" />{t("regRetry")}</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface RegisterViewProps {
  status: AgentStatus | null; reachable: boolean | null; step: string; token: string | null; compact?: boolean; readOnly: boolean;
  setToken(v: string | null): void; refresh(): Promise<void>; errText(code: string | null, status: number | null): string; syncNow(): Promise<void>;
}

/** Status of the agent and the register, and the setup step it needs next: pair, activate, recover or sign in. */
function RegisterView({ status, reachable, step, token, compact, readOnly, setToken, refresh, errText, syncNow }: RegisterViewProps) {
  const { t, lang } = useLang();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [v, setV] = useState<Record<string, string>>({});
  const field = (k: string) => ({ value: v[k] ?? "", onChange: (e: React.ChangeEvent<HTMLInputElement>) => setV((x) => ({ ...x, [k]: e.target.value })) });

  async function run(e: FormEvent, fn: () => Promise<{ ok: boolean; msg?: string; code?: string | null; status?: number | null }>) {
    e.preventDefault();
    // Pairing, activation, recovery and sign-in belong to the tab that owns the register.
    if (busy || readOnly) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      if (!r.ok) setError(errText(r.code ?? null, r.status ?? null));
      else { setV({}); if (r.msg) toast(r.msg); }
    } finally {
      setBusy(false);
      await refresh();
    }
  }
  const pair = (e: FormEvent) => run(e, async () => {
    const r = await agentCall<{ token: string }>("POST", "/pair", { code: v.pair ?? "", name: "training register" }, null);
    if (r.body?.token) setToken(r.body.token);
    return { ok: !!r.body?.token, msg: t("regPaired"), code: r.code, status: r.status };
  });
  const activate = (e: FormEvent) => run(e, async () => {
    const r = await agentCall("POST", "/activate", { code: v.activation ?? "" });
    return { ok: r.status === 200, msg: t("regActivated"), code: r.code, status: r.status };
  });
  const recover = (e: FormEvent) => run(e, async () => {
    const r = await agentCall("POST", "/recover", { code: v.recovery ?? "" });
    return { ok: r.status === 200, msg: t("regRecovered"), code: r.code, status: r.status };
  });
  const signIn = (e: FormEvent) => run(e, async () => {
    if (!/^05\d{8}$/.test((v.phone ?? "").trim())) return { ok: false, code: "mobile" };
    const r = await agentCall("POST", "/grants", { phone: (v.phone ?? "").trim(), password: v.password ?? "" });
    return { ok: r.status === 201, msg: t("regSignedIn"), code: r.code, status: r.status };
  });

  const steps: [string, string][] = [["pair", t("regStepPair")], ["activate", t("regStepActivate")], ["signin", t("regStepSignIn")]];
  const order = ["agent", "pair", "activate", "recover", "signin", "ready"];
  const form = (id: string, title: string, body: string, children: ReactNode, onSubmit: (e: FormEvent) => void, submit: string) => (
    <div className="card" data-testid={`setup-${id}`}>
      <div className="card-head"><h2>{title}</h2></div>
      <form className="card-body" onSubmit={onSubmit} noValidate>
        <p className="hint" style={{ margin: 0, fontSize: 13 }}>{body}</p>
        {children}
        {error && <div className="dlg-alert show" role="alert" style={{ margin: 0 }} data-testid="setup-error"><Icon name="alert" /><span>{error}</span></div>}
        <div><button className="btn btn-primary" type="submit" disabled={busy || readOnly}>{busy ? t("saving") : submit}</button></div>
      </form>
    </div>
  );
  const input = (id: string, label: string, props: Record<string, unknown>) => (
    <div className="field"><label htmlFor={`reg-${id}`}>{label}</label><input id={`reg-${id}`} className="control" autoComplete="off" {...props} /></div>
  );

  return (
    <div className="reg-setup">
      {step !== "ready" && (
        <div className="reg-steps">
          {steps.map(([k, l], i) => {
            const done = order.indexOf(step) > order.indexOf(k) && !(k === "signin" && step === "recover");
            return <span key={k} className={`pill ${done ? "good" : step === k ? "warn" : ""}`}>{i + 1}. {l}</span>;
          })}
        </div>
      )}
      {step === "agent" && (
        <div className="card" data-testid="setup-agent"><div className="card-head"><h2>{t("regAgentTitle")}</h2></div>
          <div className="card-body"><p style={{ margin: 0 }}>{reachable !== false ? t("loading") : t("regAgentBody", { x: agentUrl() })}</p>
            <div><button className="btn" onClick={() => { void refresh(); }}><Icon name="refresh" />{t("regTryAgain")}</button></div></div></div>
      )}
      {step === "pair" && form("pair", t("regPairTitle"), t("regPairBody"), input("pair", t("regPairingCode"), { ...field("pair"), placeholder: "XXXXX-XXXXX", className: "control num" }), pair, t("regPair"))}
      {step === "activate" && form("activate", t("regActivateTitle"), t("regActivateBody"), input("activation", t("regActivationCode"), { ...field("activation"), placeholder: "XXXXX-XXXXX", className: "control num" }), activate, t("regActivate"))}
      {step === "recover" && form("recover", t("regRecoverTitle"), t("regRecoverBody"), input("recovery", t("regRecoveryCode"), { ...field("recovery"), placeholder: "XXXXX-XXXXX", className: "control num" }), recover, t("regRecover"))}
      {step === "signin" && form("signin", t("regSignInTitle"), t("regSignInBody"), <>
        {input("phone", t("mobileNumber"), { ...field("phone"), type: "tel", inputMode: "tel", placeholder: t("mobilePh"), className: "control num", style: { textAlign: "start" } })}
        {input("password", t("password"), { ...field("password"), type: "password", placeholder: t("passwordPh") })}
      </>, signIn, t("signIn"))}
      {(!compact || step === "ready") && (
        <div className="card" data-testid="agent-status">
          <div className="card-head"><h2>{t("regStatusTitle")}</h2>
            <span style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-sm" onClick={() => { void syncNow(); }} disabled={readOnly || !status?.activated}><Icon name="refresh" />{t("regSyncNow")}</button>
              {token && <button className="btn btn-sm btn-ghost" onClick={() => setToken(null)} disabled={readOnly}>{t("regUnpair")}</button>}
            </span></div>
          <div className="card-body">
            <div className="reg-status">
              <div><span>{t("regAgent")}</span><b>{reachable ? `${t("regConnected")} · ${status?.version ?? ""}` : t("regNotReachable")}</b></div>
              <div><span>{t("regDevice")}</span><b>{status?.device ? `${status.device.code} · ${status.device.name}` : t("regNotActivated")}</b></div>
              <div><span>{t("regCredential")}</span><b data-testid="credential-state">{status ? t(`regCred_${status.credential.state}`) : "—"}</b></div>
              <div><span>{t("regCashierGrant")}</span><b>{status?.grant ? t("regGrantUntil", { x: status.grant.userName, d: new Date(status.grant.expiresAt).toLocaleString(locale(lang)) }) : t("regNoGrant")}</b></div>
              <div><span>{t("regDelta")}</span><b>{status?.lastSync ? t("regLastSync", { d: new Date(status.lastSync.at).toLocaleTimeString(locale(lang)) }) : "—"}{status?.lastSync?.stoppedAt ? ` · ${t("regSyncWaiting")}` : ""}</b></div>
              <div><span>{t("regSalesCounts")}</span><b data-testid="sales-counts">{status ? t("regCounts", { p: status.sales.pending, s: status.sales.synced, f: status.sales.failed }) : "—"}</b></div>
              <div><span>{t("regSigning")}</span><b>{t("regUnavailable")}</b></div>
              <div><span>{t("regPrinting")}</span><b>{t("regUnavailable")}</b></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
